import { useEffect, useState } from 'react';
import { initialDashboardState, startDashboardPolling, DASHBOARD_REFRESH_MS, type AdminDashboardData, type DashboardState } from './adminDashboardModel';

// Admin -> Dashboard: the networks the caller works in and an overview of the selected one, from
// GET /api/admin/dashboard. Numbers come only from the server; a section the caller may not see is
// shown as not available, never as 0.

export interface OverviewNetwork {
  id: string;
  name: string;
  tenant_name?: string;
}

export type OverviewSection = 'Radios' | 'Groups' | 'Users';

interface ViewProps {
  state: DashboardState;
  networks: OverviewNetwork[];
  networksLoaded: boolean;
  selectedId: string;
  onSelectNetwork: (id: string) => void;
  onOpen: (section: OverviewSection) => void;
}

const scopeLabel = (scope: 'network' | 'departments') => (scope === 'departments' ? 'Your departments in this network' : 'Whole network');

function Stat({ label, value, tone = 'text-white' }: { label: string; value: number; tone?: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className={`text-2xl font-semibold ${tone}`}>{value}</dd>
    </div>
  );
}

function Card({ title, icon, note, link, onOpen, children }: {
  title: string; icon: string; note?: string; link?: { label: string; section: OverviewSection }; onOpen: (s: OverviewSection) => void; children: React.ReactNode;
}) {
  return (
    <section className="bg-gray-800 rounded-lg border border-gray-700 p-5 flex flex-col gap-4" aria-label={title}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold"><span className="mr-2">{icon}</span>{title}</h3>
          {note && <p className="text-xs text-gray-400 mt-1">{note}</p>}
        </div>
        {link && (
          <button type="button" onClick={() => onOpen(link.section)} className="shrink-0 text-sm text-blue-300 hover:text-blue-200 underline-offset-2 hover:underline">
            {link.label} →
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

const NotAvailable = ({ what }: { what: string }) => (
  <p className="text-sm text-gray-400">Not available: your role does not include {what} in this network.</p>
);

function Sections({ data, onOpen }: { data: AdminDashboardData; onOpen: (s: OverviewSection) => void }) {
  const { radios, groups, network_accounts: accounts, sos } = data;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <Card title="Radios" icon="📡" note={radios ? scopeLabel(radios.scope) : undefined} link={radios ? { label: 'Open Radios', section: 'Radios' } : undefined} onOpen={onOpen}>
        {radios ? (
          <dl className="grid grid-cols-2 gap-3">
            <Stat label="Total" value={radios.total} />
            <Stat label="Connected" value={radios.connected} tone="text-green-400" />
            <Stat label="Offline" value={radios.offline} tone="text-gray-300" />
            <Stat label="Low battery" value={radios.low_battery} tone={radios.low_battery > 0 ? 'text-yellow-300' : 'text-white'} />
            <Stat label="Transmitting" value={radios.transmitting} tone="text-blue-300" />
            <Stat label="PTT blocked" value={radios.blocked} tone={radios.blocked > 0 ? 'text-orange-300' : 'text-white'} />
          </dl>
        ) : <NotAvailable what="viewing radios" />}
      </Card>
      <Card title="Groups" icon="📢" note={groups ? scopeLabel(groups.scope) : undefined} link={groups ? { label: 'Open Groups', section: 'Groups' } : undefined} onOpen={onOpen}>
        {groups ? <dl><Stat label="Total" value={groups.total} /></dl> : <NotAvailable what="viewing groups" />}
      </Card>
      <Card title="Network accounts" icon="👥" note="Accounts homed in this network (Admin → Users); customer People are managed under Settings."
        link={accounts ? { label: 'Open Users', section: 'Users' } : undefined} onOpen={onOpen}>
        {accounts ? (
          <dl className="grid grid-cols-3 gap-3">
            <Stat label="Total" value={accounts.total} />
            <Stat label="Active" value={accounts.active} tone="text-green-400" />
            <Stat label="Disabled" value={accounts.disabled} tone="text-gray-300" />
          </dl>
        ) : <NotAvailable what="user administration for the whole customer" />}
      </Card>
      <Card title="Active SOS" icon="🚨" note={sos ? `Unresolved alarms · ${scopeLabel(sos.scope)}` : undefined} onOpen={onOpen}>
        {sos ? (
          <dl><Stat label="Unresolved" value={sos.active} tone={sos.active > 0 ? 'text-red-400' : 'text-white'} /></dl>
        ) : <NotAvailable what="dispatch" />}
      </Card>
    </div>
  );
}

export function DashboardView({ state, networks, networksLoaded, selectedId, onSelectNetwork, onOpen }: ViewProps) {
  const { data, error, loading, stale } = state;
  // Only ever show the answer for the network that is selected now.
  const current = data && data.network.id === selectedId ? data : null;
  const updated = current ? new Date(current.generated_at) : null;
  return (
    <div className="space-y-6">
      <section className="bg-gray-800 rounded-lg border border-gray-700 p-5" aria-label="Networks">
        <h2 className="text-lg font-semibold">Your networks <span className="text-gray-400 font-normal">({networks.length})</span></h2>
        {!networksLoaded ? (
          <p className="text-sm text-gray-400 mt-2">Loading networks…</p>
        ) : networks.length === 0 ? (
          <p className="text-sm text-gray-400 mt-2">You do not have access to any network yet.</p>
        ) : (
          <ul className="mt-3 flex flex-wrap gap-2">
            {networks.map((n) => (
              <li key={n.id}>
                <button type="button" onClick={() => onSelectNetwork(n.id)} aria-pressed={n.id === selectedId}
                  className={`px-3 py-1.5 rounded-full text-sm border transition ${n.id === selectedId ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-900 border-gray-600 text-gray-200 hover:bg-gray-700'}`}>
                  {n.name}{n.tenant_name ? <span className="text-gray-400"> · {n.tenant_name}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedId && (
        <>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
            <div>
              <h2 className="text-2xl font-bold">{current ? current.network.name : networks.find((n) => n.id === selectedId)?.name ?? 'Network'}</h2>
              {current && (
                <p className="text-sm text-gray-300 mt-1">
                  Customer: {current.customer.name}
                  {' · '}
                  {current.scope.tenant_wide
                    ? 'Access: whole customer'
                    : `Access: departments ${current.scope.departments.map((d) => d.name).join(', ') || '(none)'} (each card shows its own scope)`}
                </p>
              )}
            </div>
            <p className="text-xs text-gray-400" aria-live="polite">
              {loading && !current ? 'Loading…' : updated ? `Updated ${updated.toLocaleTimeString()} · refreshes every ${DASHBOARD_REFRESH_MS / 1000} s` : ''}
            </p>
          </div>

          {error && (
            <div role="alert" className={`rounded-lg border p-3 text-sm ${stale && current ? 'border-yellow-700 bg-yellow-900/30 text-yellow-100' : 'border-red-800 bg-red-900/30 text-red-200'}`}>
              {stale && current ? `Refresh failed; showing the numbers from ${updated?.toLocaleTimeString()}. ${error}` : error}
            </div>
          )}

          {current ? <Sections data={current} onOpen={onOpen} /> : !error && <p className="text-gray-400">Loading dashboard…</p>}
        </>
      )}
    </div>
  );
}

export default function AdminOverview(props: Omit<ViewProps, 'state'>) {
  const { selectedId } = props;
  const [state, setState] = useState<DashboardState>(() => initialDashboardState(selectedId));
  useEffect(() => startDashboardPolling(selectedId, setState), [selectedId]);
  return <DashboardView {...props} state={state.orgId === selectedId ? state : initialDashboardState(selectedId)} />;
}
